import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const monthlyTraffic = [
  { month: 'Jan', organic: 280, paid: 0 },
  { month: 'Feb', organic: 320, paid: 180 },
  { month: 'Mar', organic: 490, paid: 310 },
  { month: 'Apr', organic: 640, paid: 420 },
  { month: 'Máj', organic: 820, paid: 580 },
  { month: 'Jún', organic: 960, paid: 640 },
  { month: 'Júl', organic: 1080, paid: 720 },
  { month: 'Aug', organic: 1180, paid: 810 },
  { month: 'Sep', organic: 1232, paid: 890 },
  { month: 'Okt', organic: 1350, paid: 960 },
  { month: 'Nov', organic: 1480, paid: 1100 },
  { month: 'Dec', organic: 1620, paid: 1380 },
];

const monthlyRevenue = [
  { month: 'Jan', revenue: 4200 },
  { month: 'Feb', revenue: 5800 },
  { month: 'Mar', revenue: 7400 },
  { month: 'Apr', revenue: 8900 },
  { month: 'Máj', revenue: 10200 },
  { month: 'Jún', revenue: 11600 },
  { month: 'Júl', revenue: 12800 },
  { month: 'Aug', revenue: 13900 },
  { month: 'Sep', revenue: 14800 },
  { month: 'Okt', revenue: 15600 },
  { month: 'Nov', revenue: 16400 },
  { month: 'Dec', revenue: 17300 },
];

const trafficSources = [
  { label: 'Organické (SEO)', value: 45, color: '#f97316' },
  { label: 'Platené (Ads)', value: 28, color: '#a78bfa' },
  { label: 'Sociálne siete', value: 17, color: '#34d399' },
  { label: 'Priame', value: 10, color: '#fbbf24' },
];

function TrafficBarChart({ inView }: { inView: boolean }) {
  const maxOrganic = Math.max(...monthlyTraffic.map((d) => d.organic + d.paid));

  return (
    <div>
      <div className="flex items-end gap-1.5 h-48 mb-3">
        {monthlyTraffic.map((d, i) => (
          <div key={d.month} className="flex-1 flex flex-col items-center justify-end gap-0.5 h-full">
            {/* Paid bar */}
            <div
              className="w-full rounded-t transition-all duration-700 bg-violet-500/50"
              style={{
                height: inView ? `${(d.paid / maxOrganic) * 100}%` : '0%',
                transitionDelay: `${i * 50}ms`,
              }}
            />
            {/* Organic bar */}
            <div
              className="w-full rounded-t bg-gradient-to-t from-orange-600 to-orange-400 transition-all duration-700"
              style={{
                height: inView ? `${(d.organic / maxOrganic) * 100}%` : '0%',
                transitionDelay: `${i * 50 + 100}ms`,
              }}
            />
          </div>
        ))}
      </div>
      {/* X axis labels */}
      <div className="flex gap-1.5">
        {monthlyTraffic.map((d) => (
          <div key={d.month} className="flex-1 text-center text-zinc-600 text-xs">{d.month}</div>
        ))}
      </div>
    </div>
  );
}

function RevenueLineChart({ inView }: { inView: boolean }) {
  const maxRevenue = Math.max(...monthlyRevenue.map((d) => d.revenue));
  const minRevenue = Math.min(...monthlyRevenue.map((d) => d.revenue));
  const chartH = 160;
  const chartW = 600;
  const padX = 10;
  const padY = 10;

  const points = monthlyRevenue.map((d, i) => {
    const x = padX + (i / (monthlyRevenue.length - 1)) * (chartW - 2 * padX);
    const y = padY + ((maxRevenue - d.revenue) / (maxRevenue - minRevenue)) * (chartH - 2 * padY);
    return { x, y, ...d };
  });

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPoints = `${points[0].x},${chartH} ${polylinePoints} ${points[points.length - 1].x},${chartH}`;

  return (
    <div>
      <div className="relative">
        {/* Y-axis grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pb-5" aria-hidden="true">
          {[maxRevenue, Math.round((maxRevenue + minRevenue) / 2), minRevenue].map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-zinc-700 text-xs w-12 text-right flex-shrink-0">€{(v / 1000).toFixed(0)}k</span>
              <div className="flex-1 border-t border-zinc-800/60 border-dashed" />
            </div>
          ))}
        </div>

        <svg
          viewBox={`0 0 ${chartW} ${chartH}`}
          className="w-full pl-14"
          style={{ height: chartH }}
          aria-label="Revenue growth chart"
        >
          <defs>
            <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0.01" />
            </linearGradient>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
            <clipPath id="revealClip">
              <rect
                x="0"
                y="0"
                width={inView ? chartW : 0}
                height={chartH}
                style={{ transition: 'width 1.8s cubic-bezier(0.4,0,0.2,1)', transitionDelay: '300ms' }}
              />
            </clipPath>
          </defs>

          {/* Area fill */}
          <polygon points={areaPoints} fill="url(#revenueGrad)" clipPath="url(#revealClip)" />

          {/* Line */}
          <polyline
            points={polylinePoints}
            fill="none"
            stroke="url(#lineGrad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            clipPath="url(#revealClip)"
          />

          {/* Dots */}
          {inView && points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="3.5"
              fill="#f97316"
              stroke="#18181b"
              strokeWidth="2"
              style={{ opacity: inView ? 1 : 0, transition: `opacity 0.3s ${0.3 + i * 0.12}s` }}
            />
          ))}
        </svg>
      </div>

      {/* X labels */}
      <div className="flex pl-14 mt-1">
        {monthlyRevenue.map((d) => (
          <div key={d.month} className="flex-1 text-center text-zinc-600 text-xs">{d.month}</div>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ inView }: { inView: boolean }) {
  const total = trafficSources.reduce((s, t) => s + t.value, 0);
  let cumulativePercent = 0;

  const segments = trafficSources.map((src) => {
    const startPercent = cumulativePercent;
    cumulativePercent += src.value / total;
    return { ...src, startPercent, endPercent: cumulativePercent };
  });

  const radius = 48;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative w-36 h-36">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90" aria-label="Traffic sources donut chart">
          {segments.map((seg, i) => {
            const dashArray = seg.endPercent * circumference - seg.startPercent * circumference;
            const dashOffset = -seg.startPercent * circumference;
            return (
              <circle
                key={seg.label}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth="16"
                strokeDasharray={`${inView ? dashArray : 0} ${circumference}`}
                strokeDashoffset={dashOffset}
                style={{
                  transition: `stroke-dasharray 1s cubic-bezier(0.4,0,0.2,1)`,
                  transitionDelay: `${i * 200}ms`,
                }}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-white font-black text-base leading-none">100%</p>
            <p className="text-zinc-600 text-xs">traffic</p>
          </div>
        </div>
      </div>

      <div className="w-full space-y-2">
        {trafficSources.map((src) => (
          <div key={src.label} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: src.color }} />
              <span className="text-zinc-400 text-xs">{src.label}</span>
            </div>
            <span className="text-white font-bold text-xs">{src.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CaseCharts() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-900/30 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-line-chart-line text-orange-400 text-sm" />
            <span className="text-zinc-300 text-sm font-semibold">Analytics & Grafy</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Rast,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              ktorý neostáva iba v prezentácii
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Každý mesiac predom s dátami. Žiadne odhady - len čísla z reálnych nástrojov.
          </p>
        </div>

        {/* Row 1: Traffic bars + Donut */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Traffic bar chart */}
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800/60 rounded-2xl p-7">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-white font-bold text-base mb-1">Mesačná návštevnosť</h3>
                <p className="text-zinc-500 text-sm">Organická + platená - január až december</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm bg-orange-500" />
                  <span className="text-zinc-500 text-xs">Organická</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm bg-violet-500/60" />
                  <span className="text-zinc-500 text-xs">Platená</span>
                </div>
              </div>
            </div>
            <TrafficBarChart inView={inView} />
          </div>

          {/* Donut */}
          <div className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-7">
            <div className="mb-6">
              <h3 className="text-white font-bold text-base mb-1">Zdroje návštevnosti</h3>
              <p className="text-zinc-500 text-sm">Rozloženie po 12 mesiacoch</p>
            </div>
            <DonutChart inView={inView} />
          </div>
        </div>

        {/* Row 2: Revenue line chart */}
        <div
          className={`bg-zinc-900 border border-zinc-800/60 rounded-2xl p-7 transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-white font-bold text-base mb-1">Vývoj mesačných tržieb</h3>
              <p className="text-zinc-500 text-sm">WooCommerce · Január–December 2024</p>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-right">
                <p className="text-zinc-600 text-xs">Začiatok</p>
                <p className="text-zinc-400 font-bold text-sm">€4 200</p>
              </div>
              <i className="ri-arrow-right-line text-zinc-700" />
              <div className="text-right">
                <p className="text-zinc-600 text-xs">Koniec roka</p>
                <p className="text-emerald-400 font-black text-lg">€17 300</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-1.5">
                <span className="text-emerald-400 font-black text-sm">+312%</span>
              </div>
            </div>
          </div>
          <RevenueLineChart inView={inView} />
        </div>
      </div>
    </section>
  );
}
